import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  CompensatoryWorkDecision,
  CompensatoryWorkRequest,
  CreateCompensatoryWorkInput,
  CompensatoryWorkStatus,
} from '../types/compensatoryWork';

type Envelope<T> = { success: boolean; message: string; data: T };

function flag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

const normalize = (r: any): CompensatoryWorkRequest => ({
  compensatoryWorkRequestId: Number(r.compensatoryWorkRequestId),
  employeeId: Number(r.employeeId),
  employeeName: r.employeeName ?? null,
  holidayDate: String(r.holidayDate).slice(0, 10),
  businessReason: String(r.businessReason),
  statusCode: r.statusCode,
  businessRequirementConfirmed:
    r.businessRequirementConfirmed == null
      ? null
      : Boolean(r.businessRequirementConfirmed),
  billableOrClientWork:
    r.billableOrClientWork == null ? null : Boolean(r.billableOrClientWork),
  revenueOrDeliveryBenefit:
    r.revenueOrDeliveryBenefit == null
      ? null
      : Boolean(r.revenueOrDeliveryBenefit),
  criticalCommitment:
    r.criticalCommitment == null ? null : Boolean(r.criticalCommitment),
  decisionComments: r.decisionComments ?? null,
  cancellationReason:
    r.cancellationReason ?? r.cancellation_reason ?? r.cancelReason ?? null,
  adjustedDays: Number(r.adjustedDays || 0),
  earnedDays: Number(r.earnedDays || 0),
  earnedLeaveTypeCode: r.earnedLeaveTypeCode ?? null,
  earnedLeaveTypeName: r.earnedLeaveTypeName ?? r.leaveTypeName ?? null,
  usedDays: Number(r.usedDays || 0),
  expiresOn: r.expiresOn ? String(r.expiresOn).slice(0, 10) : null,
  createdAt: r.createdAt ?? null,
  canCancel: flag(r.canCancel ?? r.can_cancel),
});

export async function getCompensatoryWorkRequests(
  status?: CompensatoryWorkStatus,
) {
  const r = await apiClient.get<Envelope<{ items: any[] }>>(
    API_ENDPOINTS.COMPENSATORY_WORK.LIST,
    { params: status ? { status } : undefined },
  );
  return r.data.data.items.map(normalize);
}

export async function createCompensatoryWorkRequest(
  input: CreateCompensatoryWorkInput,
) {
  const r = await apiClient.post<Envelope<{ request: any }>>(
    API_ENDPOINTS.COMPENSATORY_WORK.CREATE,
    input,
  );
  return normalize(r.data.data.request);
}

export async function decideCompensatoryWorkRequest(
  id: number,
  review: CompensatoryWorkDecision,
) {
  const r = await apiClient.post<Envelope<{ request: any }>>(
    API_ENDPOINTS.COMPENSATORY_WORK.DECIDE(id),
    {
      decision: review.businessRequirementConfirmed ? 'APPROVED' : 'REJECTED',
      ...review,
    },
  );
  return normalize(r.data.data.request);
}

export async function cancelCompensatoryWorkRequest(
  id: number,
  reason: string,
) {
  const cancellationReason = reason.trim();
  if (!cancellationReason) {
    throw Object.assign(new Error('cancellationReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const r = await apiClient.post<Envelope<{ request: any }>>(
    API_ENDPOINTS.COMPENSATORY_WORK.CANCEL(id),
    { cancellationReason },
  );
  return normalize(r.data.data.request);
}
